import type { WidgetPublicResolveBySlugResponse } from "@shared/contracts/widgets/public/resolveBySlug";
import type { WidgetLocale } from "@shared/widgets/localeResolver";
import { fetchWidget, type FetchResult } from "./api";
import { ensureShadow, isAlreadyMounted, markMounted, renderShadow } from "./dom";
import { resolveLocaleFromNavigator, createTranslator, type Translator } from "./i18n/resolve";
import { renderHeader } from "./components/header";
import { renderFooter } from "./components/footer";
import { renderEmptyState } from "./components/emptyState";
import { renderCarousel, wireCarousel, type CarouselController } from "./templates/carousel";
import { renderGrid } from "./templates/grid";
import { renderList } from "./templates/list";
import { hashJson } from "./utils/hash";

export const POLLING_INTERVAL_MS = 5 * 60 * 1000;
const WARN_PREFIX = "Ma Belle Note widget:";

type MountedWidget = {
  destroy: () => void;
};

export const isHttpsAllowed = (protocol: string, hostname: string): boolean => {
  if (protocol === "https:") return true;
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".local")) {
    return true;
  }
  return false;
};

const pickTemplate = (
  payload: Extract<WidgetPublicResolveBySlugResponse, { status: "active" }>,
  translate: Translator,
  now?: number,
): string => {
  switch (payload.config.format) {
    case "carousel":
      return renderCarousel({
        reviews: payload.reviews,
        translate,
        now,
        transition: payload.config.carouselTransition,
      });
    case "grid":
      return renderGrid({ reviews: payload.reviews, translate, now });
    case "list":
      return renderList({ reviews: payload.reviews, translate, now });
  }
};

const renderPayload = (
  shadow: ShadowRoot,
  payload: WidgetPublicResolveBySlugResponse,
  translate: Translator,
  locale: WidgetLocale,
  now?: number,
): CarouselController | null => {
  if (payload.status === "not_found") {
    shadow.innerHTML = "";
    return null;
  }
  let body = "";
  if (payload.status === "active") {
    body =
      renderHeader({
        branding: payload.branding,
        stats: payload.stats,
        translate,
        locale,
      }) +
      pickTemplate(payload, translate, now) +
      renderFooter(translate, payload.config.brandingFooterEnabled);
  } else if (payload.reason === "subscription_inactive") {
    body = "";
  } else {
    body = renderEmptyState(payload.reason, translate);
  }
  const mode = payload.status === "active" ? payload.config.mode : "light";
  renderShadow({
    shadow,
    body,
    branding: payload.branding,
    mode,
    colors:
      payload.status === "active"
        ? {
            backgroundColor: payload.config.backgroundColor,
            cardColor: payload.config.cardColor,
            textColor: payload.config.textColor,
          }
        : undefined,
    cardRadius: payload.status === "active" ? payload.config.cardRadius : undefined,
    cardBorderWidth: payload.status === "active" ? payload.config.cardBorderWidth : undefined,
    cardBorderColor: payload.status === "active" ? payload.config.cardBorderColor : undefined,
    cardShadowSize: payload.status === "active" ? payload.config.cardShadowSize : undefined,
    cardShadowDirections:
      payload.status === "active" ? payload.config.cardShadowDirections : undefined,
    carouselTransitionDurationMs:
      payload.status === "active" ? payload.config.carouselTransitionDurationMs : undefined,
  });
  if (payload.status === "active" && payload.config.format === "carousel") {
    return wireCarousel({
      root: shadow,
      translate,
      intervalMs: payload.config.carouselIntervalSeconds * 1000,
    });
  }
  return null;
};

export type BootstrapOptions = {
  fetchImpl?: typeof fetch;
  win?: Window & typeof globalThis;
  intervalMs?: number;
  now?: number;
};

export const bootstrapContainer = async (
  container: HTMLElement,
  options: BootstrapOptions = {},
): Promise<MountedWidget | null> => {
  const win = options.win ?? window;
  const doc = win.document;
  const fetchImpl = options.fetchImpl ?? win.fetch.bind(win);
  const intervalMs = options.intervalMs ?? POLLING_INTERVAL_MS;

  if (!isHttpsAllowed(win.location.protocol, win.location.hostname)) {
    win.console.warn(`${WARN_PREFIX} HTTPS required.`);
    return null;
  }
  if (isAlreadyMounted(container)) return null;

  const slug = container.dataset["slug"];
  if (!slug) {
    win.console.warn(`${WARN_PREFIX} missing data-slug attribute.`);
    return null;
  }
  markMounted(container);

  const locale = resolveLocaleFromNavigator(win.navigator.language);
  const translate = createTranslator(locale);
  const shadow = ensureShadow(container);

  let currentHash = "";
  let controller: CarouselController | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const apply = (result: FetchResult) => {
    if (result.kind === "not_found") {
      win.console.warn(`${WARN_PREFIX} widget not found for slug "${slug}".`);
      return;
    }
    if (result.kind === "error") {
      win.console.warn(`${WARN_PREFIX} fetch failed (${result.reason}).`);
      return;
    }
    const nextHash = hashJson(result.payload);
    if (nextHash === currentHash) return;
    currentHash = nextHash;
    controller?.destroy();
    controller = renderPayload(shadow, result.payload, translate, locale, options.now);
  };

  apply(await fetchWidget(slug, locale, fetchImpl));

  const refresh = async () => {
    if (doc.hidden) return;
    apply(await fetchWidget(slug, locale, fetchImpl));
  };

  pollTimer = setInterval(refresh, intervalMs);

  const handleVisibility = () => {
    if (!doc.hidden) void refresh();
  };
  doc.addEventListener("visibilitychange", handleVisibility);

  return {
    destroy: () => {
      if (pollTimer !== null) clearInterval(pollTimer);
      pollTimer = null;
      controller?.destroy();
      doc.removeEventListener("visibilitychange", handleVisibility);
    },
  };
};

export const bootstrap = async (options: BootstrapOptions = {}): Promise<MountedWidget[]> => {
  const win = options.win ?? window;
  const doc = win.document;
  if (!isHttpsAllowed(win.location.protocol, win.location.hostname)) {
    win.console.warn(`${WARN_PREFIX} HTTPS required.`);
    return [];
  }
  const containers = Array.from(doc.querySelectorAll<HTMLElement>("[data-mbn-wall]"));
  const mounted: MountedWidget[] = [];
  for (const container of containers) {
    const widget = await bootstrapContainer(container, options);
    if (widget) mounted.push(widget);
  }
  return mounted;
};

declare global {
  interface Window {
    MbnWidget?: {
      bootstrap: typeof bootstrap;
      bootstrapContainer: typeof bootstrapContainer;
    };
  }
}

if (typeof window !== "undefined") {
  window.MbnWidget = { bootstrap, bootstrapContainer };
  const start = () => {
    void bootstrap();
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
}
